const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) return backendUrl.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') return '';
  return 'http://localhost:3010';
};

const BASE = `${getApiBaseUrl()}/api/rendicion`;

class RendicionService {
  async getPendientesPorCompania() {
    const res = await fetch(`${BASE}/pendientes-por-compania`);
    if (!res.ok) throw new Error(`Error ${res.status}`);
    return res.json();
  }

  async generarYEnviar(compania_id, periodo, tipo_rendicion = 'individual') {
    const res = await fetch(`${BASE}/generar-y-enviar`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ compania_id, periodo, tipo_rendicion }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    return res.json();
  }

  async acuseRecibo(rendicionId) {
    const res = await fetch(`${BASE}/acuse-recibo/${rendicionId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    return res.json();
  }

  getDescargaUrl(rendicionId) {
    return `${BASE}/descargar/${rendicionId}`;
  }

  getModeloUrl(rendicionId) {
    return `${BASE}/modelo-respuesta/${rendicionId}`;
  }

  /**
   * Descarga un archivo del backend COMPROBANDO la respuesta antes de guardarla.
   *
   * Los dos botones de descarga eran `<a href download>`, y un ancla guarda lo
   * que venga: si el servidor responde un error, el navegador te deja el JSON en
   * Descargas con el id de la rendición por nombre. Le pasó a Claudio —bajó
   * `ee98195f-….json` con `{"error":"Sin certificados pendientes de rendir para
   * esta compañía"}` dentro— y la pantalla no dijo nada.
   *
   * Un ancla no puede mirar el status; por eso esto va por `fetch`. El nombre
   * sale del `Content-Disposition` que ya manda el backend.
   */
  async descargarArchivo(url, nombrePorDefecto) {
    const res = await fetch(url);

    if (!res.ok) {
      // El cuerpo del error puede no ser JSON (un 502 del proxy, por ejemplo).
      const detalle = await res.json().catch(() => null);
      throw new Error(detalle?.error ?? `No se pudo descargar (HTTP ${res.status})`);
    }

    // Defensa extra: un 200 con JSON tampoco es el archivo.
    const tipo = res.headers.get('content-type') ?? '';
    if (tipo.includes('application/json')) {
      const detalle = await res.json().catch(() => null);
      throw new Error(detalle?.error ?? 'El servidor no devolvió un archivo');
    }

    const disposicion = res.headers.get('content-disposition') ?? '';
    const nombre =
      /filename="?([^"]+)"?/.exec(disposicion)?.[1] ?? nombrePorDefecto;

    const blob = await res.blob();
    const enlace = document.createElement('a');
    enlace.href = URL.createObjectURL(blob);
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(enlace.href);
  }

  async previsualizarRespuesta(rendicionId, csvText) {
    const res = await fetch(`${BASE}/previsualizar-respuesta/${rendicionId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ csv: csvText }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    return res.json();
  }

  async recibirRespuesta(rendicionId, csvText) {
    const res = await fetch(`${BASE}/recibir-respuesta/${rendicionId}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ csv: csvText }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    return res.json();
  }

  async getDetalle(rendicionId) {
    const res = await fetch(`${BASE}/detalle/${rendicionId}`);
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    return res.json();
  }

  async getHistorial(compania_id) {
    const params = compania_id ? `?compania_id=${compania_id}` : '';
    const res = await fetch(`${BASE}/historial${params}`);
    if (!res.ok) throw new Error(`Error ${res.status}`);
    return res.json();
  }
}

const rendicionService = new RendicionService();

export default rendicionService;
